import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {
    useCertificatesFilter,
    useCertificatesInfiniteQuery,
} from './useCertificatesQuery';
import {useCertificatesSearch} from './useCertificatesSearch';
import {
    Certificate,
    needsAttention,
} from '../types';

// The DEFAULT sort is a per-entity judgement and stays local; HOW two values
// compare does not (that is `sortRecords`).
//
// ⚠️ `created` DESCENDING, and NOT `expirationDate`.
//
// Newest first is the ordinary console default, and it is the only sort key
// here that is both non-null (`AWSDateTime!`) and stored. `expirationDate` is
// read through to the provider and is NULL for every certificate that has not
// issued yet — sorting by it would put every pending certificate, which is
// exactly the set an operator is watching, at one end of the table under the
// nulls-last rule.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Certificate> = {
    columnAccessor: 'created',
    direction: 'desc',
};

// ⚠️ THE KEYWORD SEARCHES EVERY COVERED NAME, not just the primary.
//
// A certificate covering `example.com` and `*.example.com` and
// `a.b.example.com` is found by any of the three. Searching only `domainName`
// would hide a certificate from someone who searched for the very name it was
// requested to cover — which is the question this table exists to answer.
export const matchesKeyword = (item: Certificate, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.domainName,
        item.region,
        item.targetType,
        item.status,
        ...(item.coveredNames ?? []),
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// ⚠️ `NEEDS_ATTENTION` IS A FILTER VALUE THAT MATCHES TWO STATUSES, and that is
// the point of it existing at all.
//
// STUCK and VALIDATION_NOT_WRITTEN are different problems with different fixes,
// which is why they are separate members and are rendered differently. But
// "show me what is not progressing" is one question, and making an operator ask
// it twice is how the two states get treated as interchangeable again.
export const matchesFilters = (
    item: Certificate,
    filters: { status?: string, targetType?: string },
): boolean => {

    if (filters.status === 'NEEDS_ATTENTION') {
        if (!needsAttention(item)) {
            return false;
        }
    } else if (filters.status && item.status !== filters.status) {
        return false;
    }

    if (filters.targetType && item.targetType !== filters.targetType) {
        return false;
    }

    return true;
};

export const useCertificates = () => {

    const query = useCertificatesInfiniteQuery();

    const {filters} = useCertificatesFilter();

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword and the
    // filters, which are shared across sibling components.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<Certificate>>(DEFAULT_SORT_STATUS);

    const {keyword} = useCertificatesSearch();

    const pages = query.data?.pages ?? [];

    // The `?? []` is not defensive noise — a page object that came back partial
    // would otherwise make this flatMap throw inside a render.
    const items: Certificate[] = pages.flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ SORTING, THE KEYWORD AND THE FILTERS ARE ALL CLIENT-SIDE, over the
    // pages fetched so far. `listCertificates` takes no filter argument, so
    // there is no server-side alternative — and that is why the footer states a
    // LOADED count rather than a total.
    const visibleItems = sortRecords(
        items
            .filter(item => matchesFilters(item, filters))
            .filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    // How many loaded certificates are not progressing. Drives the banner that
    // makes a stuck certificate findable in a long list, rather than leaving an
    // operator to spot an orange badge by scrolling.
    const attentionCount = items.filter(needsAttention).length;

    return {
        // The loaded set BEFORE the keyword and filters narrow it — the window
        // the footer's count is measured against.
        items,
        visibleItems,
        attentionCount,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        // ⚠️ DERIVED FROM hasNextPage, NEVER FROM items.length.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchCertificates: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // ⚠️ THE RELOAD IS NOT A CONVENIENCE HERE, it is how an operator asks
        // "has it issued yet". Status is read through to the provider on every
        // request and nothing pushes a change to the browser, so a manual
        // refetch is the only way to see a transition before the cache expires.
        reload: () => query.refetch(),
        isReloading: query.isRefetching,
    };
};
